import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
  Image,
  Slider,
  SliderTrack,
  SliderFilledTrack
} from '@chakra-ui/react';
import CloseModalIcon from '../../../assets/close-modal-icon.svg';
import InvestIcon from '../../../assets/invest-icon.svg';
import TotalInvestIcon from '../../../assets/total-invest-icon.svg';
import CommunityIcon from '../../../assets/community-icon.svg';
import InformGrayIcon from '../../../assets/inform-gray-icon.svg';
import InformRedIcon from '../../../assets/inform-red-icon.svg';
import Button from '../../Button';
import { TooltipComponent } from '../../Tooltip';
import { followUser } from '../../../apis/discovery.api';

function InvestmentDetailsModal(props) {
  const { isOpen, onClose, data, bgColorOverlay, refetch, setOpenLearnMorePanel } = props;
  const [isFollowing, setIsFollowing] = useState(false);

  const handleFollowUser = async () => {
    try {
      setIsFollowing(true);
      const userId = data?.article?.creator?.id;
      await followUser(userId);
      await refetch();
      onClose();
    } catch (error) {
      console.error('Error updating follow status:', error);
    } finally {
      setIsFollowing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} isCentered onClose={onClose}>
      <ModalOverlay backgroundColor={bgColorOverlay || 'rgba(0, 0, 0, 0.5)'} />
      <ModalContent
        px={'15px'}
        py={5}
        bgColor={'#FAFAFA'}
        rounded={'12px'}
        maxW={{ base: '360px', md: '396px' }}
      >
        <ModalHeader p={0} w={'100%'} m={0} maxWidth={'unset'}>
          <div className="flex w-full items-center justify-between pb-2 border-b border-earth-brown-50">
            <p className="text-lg font-[800]">Investments details</p>
            <button className="w-10 h-10 flex items-center justify-center" onClick={onClose}>
              <Image className="cursor-pointer" src={CloseModalIcon} alt="Close" />
            </button>
          </div>
        </ModalHeader>
        <ModalBody py={6} px={0}>
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-col items-center gap-1">
              <Image className="w-10 h-10" src={InvestIcon} alt="Invest" />
              <p className="text-2xl font-[800] text-center text-growth-green-500">
                You have invested in this article!
              </p>
            </div>
            {data?.status === 'Frozen' && (
              <div className="flex gap-1 items-start">
                <img src={InformRedIcon} alt="" />
                <p className="text-error-red font-[600]">
                  Your investment is frozen because you unfollowed the writer. You won’t receive any
                  ad revenue, and your Seeds won’t be refunded. Re-follow to reactivate your
                  investment.
                </p>
              </div>
            )}
            <div className="text-sm flex flex-col px-4 text-center font-[600]">
              {data?.status === 'Frozen'
                ? `Re-follow to continue earning from the ${data?.currentShare}% ad revenue pool`
                : `Currently earning from the ${data?.currentShare}% ad revenue pool`}
              <span
                className="text-growth-green-500 font-[900] cursor-pointer hover:opacity-80"
                onClick={() => setOpenLearnMorePanel(true)}
              >
                Learn more
              </span>
            </div>
            <div className="flex justify-between w-full mb-2">
              <div className="flex items-center gap-1">
                <img src={CommunityIcon} className="w-5 h-5" alt="Community" />
                <p className="text-sm font-[600] ml-1">
                  {data?.article?.totalInvestors || 0}{' '}
                  {data?.article?.totalInvestors === 1 ? 'Investor' : 'Investors'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <img src={TotalInvestIcon} className="w-5 h-5" alt="Community" />
                <p className="text-sm font-[600] ml-1">
                  {data?.article?.totalArticlePointsInvested || 0} Seeds Invested
                </p>
              </div>
            </div>
            <div className="p-4 rounded-[8px] bg-white w-full flex flex-col gap-2">
              <p className="text-sm font-[800]">Seeds invested:</p>
              <Slider aria-label="slider" min={10} max={100} value={data?.pointInvested}>
                <SliderTrack h="6px" borderRadius={'20px'} bg="anti-flash-white">
                  <SliderFilledTrack
                    borderRadius={'20px'}
                    h="6px"
                    bg="linear-gradient(90deg, #C8E6C9 1.02%, #4CAF50 55.95%, #204A22 91.59%)"
                  />
                </SliderTrack>
              </Slider>
              <div className="flex justify-between items-center">
                <p className="text-sm text-02-dim-gray font-[600]">Min: 10</p>
                <p className="text-sm font-[600] text-growth-green-900">
                  {data?.pointInvested} Seeds
                </p>
                <div className="text-sm text-02-dim-gray flex gap-1 items-center font-[600]">
                  <TooltipComponent
                    maxW={'221px'}
                    description={
                      'The maximum Seeds to invest is limited to 100, regardless of your available balance.'
                    }
                    fontSize={'xs'}
                    color={'#204A22'}
                    placement={'top'}
                    distance={{ base: '80px', md: '-30px' }}
                    Icon={InformGrayIcon}
                  />
                  Max: 100
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter w="100%" p="0">
          {data?.status === 'Frozen' ? (
            <div className="flex gap-4 w-full">
              <Button onClick={onClose} className="w-full h-[42px] font-[900]" variant="outline">
                Discard
              </Button>
              <Button
                onClick={handleFollowUser}
                className="w-full h-[42px] font-[900]"
                isLoading={isFollowing}
              >
                Re-follow
              </Button>
            </div>
          ) : (
            <Button onClick={onClose} className="w-full h-[42px] font-[900]">
              Done
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default InvestmentDetailsModal;
